import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { customAxiosFetch, customFetch } from '../../helpers/customFetch';
import { setResults } from '../../ReduxSlices/resultsSlice';
import { InternalTicket, PlateClassification } from '../../Types';
import { setTicketsToBePrinted } from '../../ReduxSlices/ticketsSlice';
import { Button, Form, Table } from 'react-bootstrap';
import { FiTrash2, FiMail, FiMessageSquare } from 'react-icons/fi';
import GlobalActionModal from '../GlobalActionModal';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import UnassignedPlateNotificationModal from './UnassignedPlateNotificationModal';

const classificationOptions = [
  { value: PlateClassification.Unassigned, label: 'Unassigned' },
  { value: PlateClassification.Personal, label: 'Personal' },
  { value: PlateClassification.Business, label: 'Business' }
];

// null = no filter. Mirrors the optional hasRegistration query parameter on the API.
const registrationOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Has registration' },
  { value: 'false', label: 'Missing registration' }
];

const PrintQueue = () => {
  const [summonsNumber, setSummonsNumber] = useState<string>();
  const [showModal, setShowModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState<PlateClassification | null>(
    null
  );
  const [hasRegistration, setHasRegistration] = useState<boolean | null>(null);
  const ticketsToBePrinted = useAppSelector((state) => state?.tickets?.ticketsToBePrinted);
  const dispatch = useAppDispatch();

  function emailTicketsToPrint() {
    if (selectedClassification === null) {
      alert('Please select a classification first.');
      return;
    }

    const jsessionId = window.prompt('Please paste the JSESSIONID cookie value from NYCServ:');

    // If user clicked cancel or empty input
    if (!jsessionId) {
      alert('JSESSIONID is required to download the tickets.');
      return;
    }
    const processDescription = 'processing and emailing tickets to you';
    customFetch({
      endpoint: '/Ticket/EmailTicketsInPrintQueue',
      processDescription: processDescription,
      callback: () => {
        getTicketsInPrintQueue(selectedClassification, hasRegistration);
      },
      config: {
        method: 'POST',
        // HasRegistration must match the list filter, otherwise printing would stamp
        // PrintedAt on tickets the operator had filtered out.
        body: JSON.stringify({
          JSESSIONID: jsessionId,
          Classification: selectedClassification,
          HasRegistration: hasRegistration
        })
      },
      skipParseBody: true
    });
    // appHistory.push('home');
    dispatch(
      setResults({
        process: processDescription,
        results: 'Tickets are being processed, look out for an email from us'
      })
    );
  }

  function handleDeleteTicketInPrintQueue() {
    customAxiosFetch<{
      success: boolean;
    }>({
      endpoint: `/Ticket/delete-ticket-in-print-queue/${summonsNumber}`,
      config: { method: 'DELETE' },
      callback: (result) => {
        setShowModal(false);
        if (!result?.success) return;
        if (selectedClassification !== null) {
          getTicketsInPrintQueue(selectedClassification, hasRegistration);
        }
      }
    });
  }

  function handleOpenDeleteModal(summonsNumber: string) {
    setSummonsNumber(summonsNumber);
    setShowModal(true);
  }

  function handleCloseDeleteModal() {
    setSummonsNumber('');
    setShowModal(false);
  }

  const getTicketsInPrintQueue = useCallback(
    (classification: PlateClassification, registrationFilter: boolean | null) => {
      const params = new URLSearchParams({ classification: String(classification) });
      if (registrationFilter !== null) {
        params.append('hasRegistration', String(registrationFilter));
      }

      customFetch({
        endpoint: `/Ticket/GetTicketsInPrintQueue?${params.toString()}`,
        processDescription: 'Fetching tickets',
        callback: (tickets: InternalTicket[]) => dispatch(setTicketsToBePrinted(tickets))
      });
    },
    [dispatch]
  );

  useEffect(() => {
    // Clear tickets when component mounts (no filter selected yet)
    dispatch(setTicketsToBePrinted([]));
  }, [dispatch]);

  useEffect(() => {
    if (selectedClassification !== null) {
      getTicketsInPrintQueue(selectedClassification, hasRegistration);
    }
  }, [selectedClassification, hasRegistration, getTicketsInPrintQueue]);
  return (
    <div className="mt-5 text-center">
      {/* Classification Filter Dropdown */}
      <div className="mb-4 d-flex justify-content-center align-items-center gap-3">
        <Form.Label className="mb-0 fw-bold">Plate Classification:</Form.Label>
        <Form.Select
          style={{ width: '200px' }}
          value={selectedClassification ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedClassification(value === '' ? null : (Number(value) as PlateClassification));
          }}>
          <option value="">Select Classification</option>
          {classificationOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
        <Form.Label className="mb-0 fw-bold">Registration:</Form.Label>
        <Form.Select
          style={{ width: '200px' }}
          value={hasRegistration === null ? '' : String(hasRegistration)}
          onChange={(e) => {
            const value = e.target.value;
            setHasRegistration(value === '' ? null : value === 'true');
          }}>
          {registrationOptions.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </div>

      {/* Notification Button for Unassigned Plates */}
      {selectedClassification === PlateClassification.Unassigned && (
        <div className="mb-4 p-3 bg-light rounded border">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">Request Registration Information</h5>
              <p className="text-muted mb-0 small">
                Send SMS or Email to clients with unassigned plates requesting registration details
              </p>
            </div>
            <Button
              variant="primary"
              onClick={() => setShowNotificationModal(true)}
              className="d-flex align-items-center gap-2">
              <FiMail />
              <FiMessageSquare />
              <span>Send Notifications</span>
            </Button>
          </div>
        </div>
      )}

      {selectedClassification === null ? (
        <h3>Please select a classification to view the print queue</h3>
      ) : ticketsToBePrinted && ticketsToBePrinted.length > 0 ? (
        <>
          <h3>{ticketsToBePrinted.length} Ticket(s) are Ready to be Printed</h3>
          <div
            style={{
              overflowX: 'hidden',
              overflowY: 'scroll',
              maxHeight: window.innerHeight / 1.5
            }}>
            <Table striped bordered>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'white',
                  zIndex: 2
                }}>
                <tr>
                  <th>Summons Number</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {ticketsToBePrinted.map((ticket) => {
                  return (
                    <tr key={ticket.id}>
                      <td>{ticket.summonsNumber}</td>
                      <td>
                        <Button
                          variant="danger"
                          onClick={() => handleOpenDeleteModal(ticket.summonsNumber)}>
                          <FiTrash2 className="bg-red"></FiTrash2>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <ConfirmDialog
            isOpen={showModal}
            label={<>Delete ticket in Print Queue</>}
            body={
              <>
                Are you sure you want to delete this ticket? This will remove this ticket from the
                print queue.
              </>
            }
            confirmText={'Delete'}
            confirmAction={handleDeleteTicketInPrintQueue}
            handleClose={handleCloseDeleteModal}
          />

          <GlobalActionModal
            actionText="Are you sure you want to print these tickets?"
            warningText="This will remove these tickets from the print queue"
            proceedCallback={emailTicketsToPrint}
            buttonText="Print Tickets"
          />
        </>
      ) : (
        <h3>No Tickets are ready to Print for this classification</h3>
      )}

      {/* Unassigned Plate Notification Modal */}
      <UnassignedPlateNotificationModal
        show={showNotificationModal}
        onHide={() => setShowNotificationModal(false)}
        onNotificationsSent={() => {
          if (selectedClassification !== null) {
            getTicketsInPrintQueue(selectedClassification, hasRegistration);
          }
        }}
      />
    </div>
  );
};

export default PrintQueue;
